/**
 * @file Board-control menu and button rendering helpers.
 */

import { getBoardViewSettings } from "../../app/state/state.js";
import {
  getBoardGroupLabel,
  getBoardGroupOptions,
} from "./boardGrouping.js";
import {
  getFilterDefinition,
  getFilterDefinitions,
  getFilterOptionCounts,
  getFilterOptions,
  getFilterValueCount,
  getTotalActiveFilterCount,
  getSortOptions,
} from "./boardRuntime.js";
import {
  ACTIVE_TEXT_CLASS,
  DATE_MODE_LABELS,
  escapeHtml,
  getIconButtonClasses,
  getOrderedColumnEntries,
} from "./boardControlDefinitions.js";
import {
  getActiveDateGroupingMode,
  getActiveGroupBy,
  getOpenMenuState,
} from "./boardControlState.js";

const BOARD_MENU_PANEL_CLASSES = [
  "board-control-menu hidden absolute z-20 rounded-2xl border border-gray-200",
  "bg-white p-3 shadow-xl",
].join(" ");

const MENU_ROW_CLASSES = [
  "flex w-full items-center rounded-xl px-3 py-2 text-left text-sm",
  "transition-colors",
].join(" ");

function getMenuWidth(menuId) {
  return menuId === "filter" ? 256 : 240;
}

function getMenuPanelStyle(menuId) {
  return `width: ${getMenuWidth(menuId)}px;`;
}

function getInactiveRowClasses(isActive) {
  return isActive ? ACTIVE_TEXT_CLASS : "text-gray-700 hover:bg-gray-50";
}

function renderVisualCheckbox({checked, className, dataAttributes = "", value = ""}) {
  return `
    <span class="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
      <input
        type="checkbox"
        class="${className} peer sr-only"
        value="${escapeHtml(value)}"
        ${dataAttributes}
        ${checked ? "checked" : ""}
      >
      <span
        class="inline-flex h-4 w-4 items-center justify-center rounded border transition-colors peer-focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
        style="background-color: ${checked ? "var(--dfc-brand-accent)" : "#ffffff"}; border-color: ${checked ? "var(--dfc-brand-accent)" : "#d1d5db"}; color: ${checked ? "#ffffff" : "transparent"};"
        aria-hidden="true"
      >
        <span class="material-symbols-outlined text-[13px] leading-none">check</span>
      </span>
    </span>
  `;
}

/**
 * Renders a board-control menu button.
 * @param {object} options - Menu button options.
 * @param {string} options.viewKey - Board view key.
 * @param {string} options.menuId - Menu id.
 * @param {string} options.icon - Material icon.
 * @param {boolean} [options.active] - Whether the menu is active.
 * @param {boolean} [options.hidden] - Whether to hide the button.
 * @param {number} [options.badgeCount] - Active badge count.
 * @param {boolean} [options.clearable] - Whether to render the clear affordance.
 * @returns {string} Menu button HTML.
 */
export function renderMenuButton({
  viewKey,
  menuId,
  icon,
  active = false,
  hidden = false,
  badgeCount = 0,
  clearable = false,
}) {
  if (hidden) {return "";}

  return `
    <div class="relative" data-control-wrapper="${menuId}">
      <button
        type="button"
        class="${getIconButtonClasses(active)}"
        data-control-trigger="${menuId}"
        data-view-key="${viewKey}"
        data-persistent-active="${active ? "true" : "false"}"
      >
        <span class="material-symbols-outlined pointer-events-none text-[20px]">${icon}</span>
      </button>
      ${clearable && active ? `
        <button
          type="button"
          class="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-transparent bg-white text-[11px] leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
          data-control-clear="${menuId}"
          data-view-key="${viewKey}"
          aria-label="Clear ${menuId}"
        >
          <span class="pointer-events-none">x</span>
        </button>
      ` : ""}
      ${badgeCount > 0 ? `<span class="pointer-events-none absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white">${badgeCount}</span>` : ""}
      <div
        class="${BOARD_MENU_PANEL_CLASSES}"
        data-control-menu="${menuId}"
        style="${getMenuPanelStyle(menuId)}"
      ></div>
    </div>
  `;
}

/**
 * Renders a board-control action button.
 * @param {object} options - Action button options.
 * @param {string} options.viewKey - Board view key.
 * @param {string} options.action - Action id.
 * @param {string} options.icon - Material icon.
 * @returns {string} Action button HTML.
 */
export function renderActionButton({ viewKey, action, icon }) {
  return `
    <button
      type="button"
      class="${getIconButtonClasses(false)}"
      data-control-action="${action}"
      data-view-key="${viewKey}"
    >
      <span class="material-symbols-outlined pointer-events-none text-[20px]">${icon}</span>
    </button>
  `;
}

function renderColumnsMenu(viewKey) {
  const settings = getBoardViewSettings(viewKey);
  const visibleColumns = settings?.visibleColumns || [];

  return `
    <div class="mb-2 text-sm font-semibold text-gray-500">Columns</div>
    <div class="space-y-1">
      ${getOrderedColumnEntries(viewKey).map(([key, label]) => `
        <label class="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50">
          ${renderVisualCheckbox({
            checked: visibleColumns.includes(key),
            className: "board-column-checkbox",
            dataAttributes: `data-view-key="${escapeHtml(viewKey)}" data-column-key="${escapeHtml(key)}"`,
            value: key,
          })}
          <span class="min-w-0 truncate">${escapeHtml(label)}</span>
        </label>
      `).join("")}
    </div>
  `;
}

function renderGroupMenu(viewKey) {
  const openMenuState = getOpenMenuState();
  if (openMenuState?.menuId === "group" &&
      openMenuState?.viewKey === viewKey &&
      openMenuState?.pageKey === "dateMode") {
    return renderDateModeGroupPage(viewKey);
  }

  const activeGroupBy = getActiveGroupBy(viewKey);
  const groupOptions = getBoardGroupOptions(viewKey);
  const activeDateMode = getActiveDateGroupingMode(viewKey);

  return `
    <div class="mb-2 text-sm font-semibold text-gray-500">Group</div>
    <div class="space-y-1">
      ${groupOptions.map((value) => `
        <button
          type="button"
          class="${MENU_ROW_CLASSES} justify-between ${getInactiveRowClasses(activeGroupBy === value)}"
          data-group-option="${value}"
          data-view-key="${viewKey}"
        >
          <span class="min-w-0 truncate">
            <span>${escapeHtml(getBoardGroupLabel(value))}</span>
            ${value === "date" && activeGroupBy === value ? `
              <span class="ml-1 text-xs text-gray-400">(${escapeHtml(DATE_MODE_LABELS[activeDateMode] || activeDateMode)})</span>
            ` : ""}
          </span>
          ${activeGroupBy === value ? `<span class="material-symbols-outlined shrink-0 text-[18px] ${ACTIVE_TEXT_CLASS}">check</span>` : ""}
        </button>
      `).join("")}
    </div>
  `;
}

function renderMenuHeading(title) {
  return `
    <div class="mb-2 text-sm font-semibold text-gray-500">${title}</div>
  `;
}

function renderCountBadge(count, label) {
  if (count <= 0) {return "";}
  return `
    <span
      class="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white"
      aria-label="${escapeHtml(label)}"
    >
      ${count}
    </span>
  `;
}

function renderSortMenu(viewKey) {
  const settings = getBoardViewSettings(viewKey);
  const activeSortKey = settings?.sortBy || "";
  const activeDirection = settings?.sortDirection || "asc";
  const activeDirectionGlyph = activeDirection === "desc" ? "▼" : "▲";

  return `
    ${renderMenuHeading("Sort")}
    <div class="space-y-1">
      ${getSortOptions(viewKey).map((option) => `
        <button
          type="button"
          class="${MENU_ROW_CLASSES} ${getInactiveRowClasses(activeSortKey === option.key)}"
          data-sort-option="${option.key}"
          data-view-key="${viewKey}"
        >
          <span class="inline-flex min-w-0 items-center gap-1.5">
            <span class="truncate">${escapeHtml(option.label)}</span>
            ${activeSortKey === option.key ? `<span class="text-[11px] leading-none ${ACTIVE_TEXT_CLASS}">${activeDirectionGlyph}</span>` : ""}
          </span>
        </button>
      `).join("")}
    </div>
    <button
      type="button"
      class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none"
      data-sort-clear="${viewKey}"
    >
      Clear Sort
    </button>
  `;
}

function renderDateModeGroupPage(viewKey) {
  const activeDateMode = getActiveDateGroupingMode(viewKey);

  return `
    <button type="button" class="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--dfc-brand-primary)] focus:outline-none" data-group-back="${viewKey}">
      <span class="material-symbols-outlined text-[18px]">arrow_back</span>
      <span>Group</span>
    </button>
    <div class="mb-2 text-sm font-semibold text-gray-500">Date grouping</div>
    <div class="space-y-1">
      ${Object.entries(DATE_MODE_LABELS).map(([value, label]) => `
        <button
          type="button"
          class="${MENU_ROW_CLASSES} justify-between ${getInactiveRowClasses(activeDateMode === value)}"
          data-date-mode-option="${value}"
          data-view-key="${viewKey}"
        >
          <span>${escapeHtml(label)}</span>
          ${activeDateMode === value ? `<span class="material-symbols-outlined text-[18px] ${ACTIVE_TEXT_CLASS}">check</span>` : ""}
        </button>
      `).join("")}
    </div>
  `;
}

function renderFilterCategoryMenu(viewKey) {
  const settings = getBoardViewSettings(viewKey);
  const totalActiveCount = getTotalActiveFilterCount(viewKey);
  return `
    <div class="mb-2 flex items-center justify-between gap-3">
      <div class="text-sm font-semibold text-gray-500">Filter</div>
      ${renderCountBadge(totalActiveCount, `${totalActiveCount} selected filters`)}
    </div>
    <div class="space-y-1">
      ${getFilterDefinitions(viewKey).map((definition) => {
        const activeCount = getFilterValueCount(
          settings?.filters?.[definition.key],
          definition,
        );
        return `
          <button
            type="button"
            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            data-filter-category="${definition.key}"
            data-view-key="${viewKey}"
          >
            <span>${escapeHtml(definition.label)}</span>
            ${activeCount > 0 ? `<span class="text-xs font-semibold ${ACTIVE_TEXT_CLASS}">${activeCount}</span>` : ""}
          </button>
        `;
      }).join("")}
    </div>
    <button
      type="button"
      class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none"
      data-filter-clear-all="${viewKey}"
    >
      Clear Filters
    </button>
  `;
}

function renderFilterDetailMenu(viewKey, filterKey) {
  const settings = getBoardViewSettings(viewKey);
  const filterDefinition = getFilterDefinition(viewKey, filterKey);
  if (!filterDefinition) {return renderFilterCategoryMenu(viewKey);}

  const currentValue = settings?.filters?.[filterKey] || [];
  const selectedCount = getFilterValueCount(currentValue, filterDefinition);
  const detailHeader = `
    <button type="button" class="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--dfc-brand-primary)] focus:outline-none" data-filter-back="${viewKey}">
      <span class="material-symbols-outlined text-[18px]">arrow_back</span>
      <span>Filter</span>
    </button>
    <div class="mb-2 flex items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2">
        <div class="min-w-0 truncate text-sm font-semibold text-gray-500">${escapeHtml(filterDefinition.label)}</div>
        ${renderCountBadge(selectedCount, `${selectedCount} selected ${filterDefinition.label} filters`)}
      </div>
      ${selectedCount > 0 ? `
        <button
          type="button"
          class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-transparent bg-white text-xs leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
          data-filter-clear-category="${filterKey}"
          data-view-key="${viewKey}"
          aria-label="Clear ${escapeHtml(filterDefinition.label)} filter"
        >
          <span class="pointer-events-none">x</span>
        </button>
      ` : ""}
    </div>
  `;
  if (filterDefinition.kind === "date") {
    const [fromValue = "", toValue = ""] = Array.isArray(currentValue) ? currentValue : ["", ""];
    return `
      ${detailHeader}
      <div class="space-y-3">
        <label class="block text-sm text-gray-600">
          <span class="mb-1 block">From</span>
          <input type="date" class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none hover:border-[var(--dfc-brand-primary)] focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-filter-date-from="${filterKey}" data-view-key="${viewKey}" value="${escapeHtml(fromValue)}">
        </label>
        <label class="block text-sm text-gray-600">
          <span class="mb-1 block">To</span>
          <input type="date" class="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none hover:border-[var(--dfc-brand-primary)] focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-filter-date-to="${filterKey}" data-view-key="${viewKey}" value="${escapeHtml(toValue)}">
        </label>
      </div>
      <div class="mt-3 flex gap-2">
        <button type="button" class="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none" data-filter-date-clear="${filterKey}" data-view-key="${viewKey}">Clear</button>
        <button type="button" class="flex-1 rounded-xl border border-[var(--dfc-brand-accent)] px-3 py-2 text-sm ${ACTIVE_TEXT_CLASS} focus:outline-none" data-filter-date-apply="${filterKey}" data-view-key="${viewKey}">Apply</button>
      </div>
    `;
  }

  const options = getFilterOptions(viewKey, filterKey);
  const optionCounts = getFilterOptionCounts(viewKey, filterKey);
  return `
    ${detailHeader}
    <div class="max-h-64 space-y-1 overflow-y-auto pr-1">
      ${options.map((option) => `
        <label class="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50">
          ${renderVisualCheckbox({
            checked: currentValue.includes(String(option.value)),
            className: "board-filter-checkbox",
            dataAttributes: `data-view-key="${escapeHtml(viewKey)}" data-filter-key="${escapeHtml(filterKey)}"`,
            value: String(option.value),
          })}
          <span class="min-w-0 flex-1 truncate">${escapeHtml(option.text)}</span>
          <span class="shrink-0 text-xs text-gray-400">(${optionCounts.get(String(option.value)) || 0})</span>
        </label>
      `).join("")}
    </div>
  `;
}

/**
 * Returns HTML for a board-control menu.
 * @param {string} menuId - Menu id.
 * @param {string} viewKey - Board view key.
 * @returns {string} Menu HTML.
 */
export function renderControlMenu(menuId, viewKey) {
  switch (menuId) {
    case "group":
      return renderGroupMenu(viewKey);
    case "sort":
      return renderSortMenu(viewKey);
    case "filter": {
      const openMenuState = getOpenMenuState();
      return openMenuState?.menuId === "filter" &&
        openMenuState?.viewKey === viewKey &&
        openMenuState?.categoryKey ?
        renderFilterDetailMenu(viewKey, openMenuState.categoryKey) :
        renderFilterCategoryMenu(viewKey);
    }
    case "columns":
      return renderColumnsMenu(viewKey);
    default:
      return "";
  }
}
